import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { adminUsersApi } from '@/features/workspace/api';
import type { WorkspaceModule } from '@/features/workspace/types';
import type {
  AccessRequestAdmin,
  AdminUserCatalog,
  AdminUserKpi,
  AdminUserListItem,
} from '@/features/owner/adminUsersTypes';
import {
  UserDetailModal,
  UserEditModal,
  UserHistoryModal,
  UserInvitationModal,
  UserMessageModal,
  UserStatusModal,
} from './AdminUserModals';
import {
  AccessApproveModal,
  AccessRejectModal,
  AddUserModal,
  BulkAddModal,
} from './AdminUserWizards';
import { formatRoleLabel } from '@/lib/navigation';

type ActiveTab = 'users' | 'access_requests';
type RowModal = 'open' | 'edit' | 'status' | 'message' | 'invitation' | 'history';

function catalogLabel(
  items: Array<{ code: string; label: Record<string, string> }> | undefined,
  code: string,
  locale: string,
): string {
  const hit = items?.find((item) => item.code === code);
  return hit?.label?.[locale] || hit?.label?.ru || formatRoleLabel(code);
}
interface AdminUsersPageProps {
  module?: WorkspaceModule;
  breadcrumb: string;
  embedded?: boolean;
  onClose?: () => void;
  onBack?: () => void;
}

const KPI_KEYS: { key: keyof AdminUserKpi; labelKey: string }[] = [
  { key: 'total', labelKey: 'admin.users.kpi.total' },
  { key: 'active_categories', labelKey: 'admin.users.kpi.activeCategories' },
  { key: 'multi_role', labelKey: 'admin.users.kpi.multiRole' },
  { key: 'added_today', labelKey: 'admin.users.kpi.addedToday' },
  { key: 'pending_access_requests', labelKey: 'admin.users.kpi.pendingRequests' },
  { key: 'suspended', labelKey: 'admin.users.kpi.suspended' },
];

const AdminUsersPage = ({
  module,
  breadcrumb,
  embedded = false,
  onClose,
  onBack,
}: AdminUsersPageProps) => {
  const { t, locale } = useI18n();
  const { notify } = useToast();

  const [tab, setTab] = useState<ActiveTab>('users');
  const [catalog, setCatalog] = useState<AdminUserCatalog | null>(null);
  const [items, setItems] = useState<AdminUserListItem[]>([]);
  const [kpi, setKpi] = useState<AdminUserKpi | null>(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [sort, setSort] = useState('NEWEST');

  const [queue, setQueue] = useState<AccessRequestAdmin[]>([]);
  const [queueFilter, setQueueFilter] = useState<'pending' | 'decided' | 'all'>('pending');
  const [activeRequest, setActiveRequest] = useState<AccessRequestAdmin | null>(null);

  const [activeUser, setActiveUser] = useState<AdminUserListItem | null>(null);
  const [activeModal, setActiveModal] = useState<RowModal | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [decisionRequest, setDecisionRequest] = useState<{
    request: AccessRequestAdmin;
    kind: 'approve' | 'reject';
  } | null>(null);

  const loadCatalog = useCallback(() => {
    adminUsersApi.catalog().then(setCatalog).catch(() => undefined);
  }, []);

  const loadUsers = useCallback(() => {
    setLoading(true);
    adminUsersApi
      .list({ search: search || undefined, category, status, sort })
      .then((data) => {
        setItems(data.items);
        setKpi(data.kpi);
      })
      .catch(() => notify(t('common.error'), 'error'))
      .finally(() => setLoading(false));
  }, [search, category, status, sort, notify, t]);

  const loadQueue = useCallback((filter?: 'pending' | 'decided' | 'all') => {
    adminUsersApi
      .accessQueue(filter)
      .then(setQueue)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (tab === 'access_requests') loadQueue(queueFilter);
  }, [tab, queueFilter, loadQueue]);

  useEffect(() => {
    if (!embedded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [embedded]);

  const openRowModal = (user: AdminUserListItem, modal: RowModal) => {
    setActiveUser(user);
    setActiveModal(modal);
  };

  const closeRowModal = () => {
    setActiveUser(null);
    setActiveModal(null);
  };

  const refreshAfterChange = () => {
    loadUsers();
    loadQueue(queueFilter);
  };

  const handleClose = onClose ?? onBack;
  const initials = (name: string) =>
    name
      .split(/\s+/)
      .map((part) => part[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';

  const kpiColors = ['#41d2ff', '#49ddb7', '#9e80ff', '#e7b13a', '#ff8b75', '#ff6e56'];

  const usersPanel = (
    <>
      <section className="owner-access-panel">
        <header className="owner-access-panel-head">
          <div>
            <h2>Категории, фильтры и поиск</h2>
            <p>Поиск и сортировка применяются к текущему отфильтрованному набору.</p>
          </div>
        </header>
        <div className="owner-access-panel-body">
          <div className="owner-access-filterbar">
            <input
              type="search"
              placeholder="Имя, фамилия, email или Academia ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <select value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="ALL">{t('admin.users.filter.allCategories')}</option>
              {catalog?.categories.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label[locale]}
                </option>
              ))}
            </select>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="ALL">{t('admin.users.filter.allStatuses')}</option>
              {catalog?.statuses.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label[locale]}
                </option>
              ))}
            </select>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              {catalog?.sorts.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label[locale]}
                </option>
              ))}
            </select>
            <button type="button" className="owner-access-btn" onClick={() => setTab('access_requests')}>
              Открыть заявки
            </button>
          </div>
        </div>
      </section>

      <section className="owner-access-panel">
        <header className="owner-access-panel-head">
          <div>
            <h2>Найдено: {items.length}</h2>
            <p>Единый список пользователей портала.</p>
          </div>
        </header>
        <div className="owner-access-panel-body">
          {loading ? (
            <div className="owner-access-empty">{t('common.loading')}</div>
          ) : items.length === 0 ? (
            <div className="owner-access-empty">Нет пользователей по выбранным условиям.</div>
          ) : (
            <div className="owner-access-list">
              {items.map((user) => (
                <article
                  key={user.id}
                  className="owner-access-row"
                  style={{ ['--accent' as string]: '#9e80ff' }}
                >
                  <span />
                  <div className="owner-access-avatar">{initials(user.full_name || '?')}</div>
                  <div className="owner-access-main">
                    <strong>{user.full_name.trim() || user.email}</strong>
                    <small>
                      {user.academia_id} · {user.status}
                    </small>
                    {user.is_sole_system_owner ? (
                      <small style={{ color: '#e2ad3a' }}>{t('admin.users.soleOwner')}</small>
                    ) : null}
                  </div>
                  <div className="owner-access-cell">
                    <strong>{user.email || '—'}</strong>
                    <small>{user.roles.length} ролей</small>
                  </div>
                  <div className="owner-access-cell">
                    {user.roles.map((role) => (
                      <span key={role} className="owner-access-chip">
                        {catalogLabel(catalog?.roles, role, locale) || formatRoleLabel(role)}
                      </span>
                    ))}
                  </div>
                  <div className="owner-access-cell">
                    <span
                      className={`owner-access-chip${
                        user.status === 'ACTIVE'
                          ? ' mint'
                          : user.status === 'SUSPENDED'
                            ? ' red'
                            : ' gold'
                      }`}
                    >
                      {t(`admin.users.status.${user.status}` as never)}
                    </span>
                  </div>
                  <div className="owner-access-actions">
                    <button type="button" className="owner-access-btn" onClick={() => openRowModal(user, 'edit')}>
                      {t('admin.users.action.edit')}
                    </button>
                    <button type="button" className="owner-access-btn" onClick={() => openRowModal(user, 'open')}>
                      {t('admin.users.action.open')}
                    </button>
                    <button type="button" className="owner-access-btn" onClick={() => openRowModal(user, 'status')}>
                      {t('admin.users.action.status')}
                    </button>
                    <button type="button" className="owner-access-btn" onClick={() => openRowModal(user, 'message')}>
                      {t('admin.users.action.message')}
                    </button>
                    <button type="button" className="owner-access-btn" onClick={() => openRowModal(user, 'invitation')}>
                      {t('admin.users.action.invitation')}
                    </button>
                    <button type="button" className="owner-access-btn" onClick={() => openRowModal(user, 'history')}>
                      {t('admin.users.action.history')}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );

  const requestsPanel = (
    <section className="owner-access-panel">
      <header className="owner-access-panel-head">
        <div>
          <h2>Заявки на доступ</h2>
          <p>Кликните на заявку для просмотра полной информации и принятия решения.</p>
        </div>
        <nav className="owner-access-tabs" style={{ marginLeft: 'auto', border: 'none', background: 'none', padding: 0 }}>
          {(['pending', 'decided', 'all'] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={queueFilter === f ? 'active' : undefined}
              onClick={() => { setQueueFilter(f); setActiveRequest(null); }}
            >
              {f === 'pending' ? 'Ожидают' : f === 'decided' ? 'Рассмотренные' : 'Все'}
            </button>
          ))}
        </nav>
      </header>
      <div className="oar-layout">
        {/* === LIST === */}
        <div className="oar-list">
          {queue.length === 0 ? (
            <div className="owner-access-empty">{t('common.notFound')}</div>
          ) : (
            queue.map((request) => {
              const isPending = request.status === 'PENDING_OWNER_REVIEW';
              const isApproved = request.status === 'APPROVED';
              const isActive = activeRequest?.id === request.id;
              const accentColor = isPending ? '#e2ad3a' : isApproved ? '#49ddb7' : '#ff8b75';
              const statusLabel = isPending ? 'Ожидает' : isApproved ? 'Одобрена' : 'Отклонена';
              const statusClass = isPending ? 'gold' : isApproved ? 'mint' : 'red';
              return (
                <button
                  key={request.id}
                  type="button"
                  className={`oar-card${isActive ? ' oar-card--active' : ''}`}
                  style={{ ['--accent' as string]: accentColor }}
                  onClick={() => setActiveRequest(isActive ? null : request)}
                >
                  <span className="oar-card-stripe" />
                  <div className="oar-card-avatar">{initials(request.full_name || '?')}</div>
                  <div className="oar-card-body">
                    <div className="oar-card-name">{request.full_name}</div>
                    <div className="oar-card-email">{request.email}</div>
                    {request.comment && (
                      <div className="oar-card-comment">💬 {request.comment}</div>
                    )}
                  </div>
                  <div className="oar-card-meta">
                    <span className={`owner-access-chip ${statusClass}`}>{statusLabel}</span>
                    <span className="owner-access-chip gold" style={{ textTransform: 'none', letterSpacing: 0 }}>
                      {request.category_code.replace(/_/g, ' ')}
                    </span>
                    <span className="oar-card-date">
                      {request.submitted_at
                        ? new Date(request.submitted_at).toLocaleDateString(locale)
                        : '—'}
                    </span>
                  </div>
                  <div className="oar-card-chevron">{isActive ? '✕' : '›'}</div>
                </button>
              );
            })
          )}
        </div>

        {/* === DETAIL PANEL === */}
        {activeRequest && (() => {
          const req = activeRequest;
          const isPending = req.status === 'PENDING_OWNER_REVIEW';
          const isApproved = req.status === 'APPROVED';
          const langFull = req.locale === 'kz' ? 'Қазақша' : req.locale === 'en' ? 'English' : 'Русский';
          const fields: Array<{ label: string; value: string | null | undefined }> = [
            { label: 'Референс-код', value: req.reference_code },
            { label: 'Категория', value: req.category_code.replace(/_/g, ' ') },
            { label: 'Язык интерфейса', value: langFull },
            { label: 'Email', value: req.email },
            { label: 'Год поступления', value: req.admission_year ? String(req.admission_year) : null },
            { label: 'Программа', value: req.program_code },
            { label: 'Период', value: req.admission_period },
            { label: 'Уровень образования', value: req.education_level },
            { label: 'Специальность', value: req.specialty_code },
            { label: 'Подана', value: req.submitted_at ? new Date(req.submitted_at).toLocaleString(locale) : null },
            { label: 'Решение', value: req.decided_at ? new Date(req.decided_at).toLocaleString(locale) : null },
          ].filter((f) => f.value);

          return (
            <aside className="oar-detail">
              <div className="oar-detail-head">
                <div className="oar-detail-avatar">{initials(req.full_name || '?')}</div>
                <div>
                  <div className="oar-detail-name">{req.full_name}</div>
                  <div className="oar-detail-email">{req.email}</div>
                </div>
                <button
                  type="button"
                  className="owner-access-btn oar-detail-close"
                  onClick={() => setActiveRequest(null)}
                  aria-label="Закрыть"
                >
                  ✕
                </button>
              </div>

              <div className="oar-detail-status-row">
                {isPending ? (
                  <span className="owner-access-chip gold">⏳ Ожидает решения</span>
                ) : isApproved ? (
                  <span className="owner-access-chip mint">✓ Одобрена</span>
                ) : (
                  <span className="owner-access-chip red">✕ Отклонена</span>
                )}
                <span className="owner-access-chip" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)' }}>
                  # {req.reference_code}
                </span>
              </div>

              {req.comment && (
                <div className="oar-detail-comment">
                  <div className="oar-detail-section-label">Комментарий заявителя</div>
                  <p>{req.comment}</p>
                </div>
              )}

              {!isApproved && !isPending && req.decision_note && (
                <div className="oar-detail-comment" style={{ borderColor: '#ff8b75' }}>
                  <div className="oar-detail-section-label">Причина отклонения</div>
                  <p>{req.decision_note}</p>
                </div>
              )}

              <div className="oar-detail-section-label">Данные заявки</div>
              <dl className="oar-detail-fields">
                {fields.map(({ label, value }) => (
                  <div key={label} className="oar-detail-field">
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>

              {isPending && (
                <div className="oar-detail-actions">
                  <button
                    type="button"
                    className="owner-access-btn gold"
                    style={{ flex: 1 }}
                    onClick={() => { setDecisionRequest({ request: req, kind: 'approve' }); }}
                  >
                    ✓ Одобрить заявку
                  </button>
                  <button
                    type="button"
                    className="owner-access-btn danger"
                    style={{ flex: 1 }}
                    onClick={() => { setDecisionRequest({ request: req, kind: 'reject' }); }}
                  >
                    ✕ Отклонить
                  </button>
                </div>
              )}
            </aside>
          );
        })()}
      </div>
    </section>
  );

  const modals = (
    <>
      {activeUser && activeModal === 'open' && (
        <UserDetailModal user={activeUser} onClose={closeRowModal} onChanged={refreshAfterChange} />
      )}
      {activeUser && activeModal === 'edit' && catalog && (
        <UserEditModal user={activeUser} catalog={catalog} onClose={closeRowModal} onChanged={refreshAfterChange} />
      )}
      {activeUser && activeModal === 'status' && (
        <UserStatusModal user={activeUser} onClose={closeRowModal} onChanged={refreshAfterChange} />
      )}
      {activeUser && activeModal === 'message' && (
        <UserMessageModal user={activeUser} onClose={closeRowModal} onChanged={refreshAfterChange} />
      )}
      {activeUser && activeModal === 'invitation' && (
        <UserInvitationModal user={activeUser} onClose={closeRowModal} onChanged={refreshAfterChange} />
      )}
      {activeUser && activeModal === 'history' && (
        <UserHistoryModal user={activeUser} onClose={closeRowModal} onChanged={refreshAfterChange} />
      )}
      {showAdd && catalog && (
        <AddUserModal catalog={catalog} onClose={() => setShowAdd(false)} onCreated={refreshAfterChange} />
      )}
      {showBulk && catalog && (
        <BulkAddModal catalog={catalog} onClose={() => setShowBulk(false)} onCreated={refreshAfterChange} />
      )}
      {decisionRequest?.kind === 'approve' && catalog && (
        <AccessApproveModal
          request={decisionRequest.request}
          catalog={catalog}
          onClose={() => setDecisionRequest(null)}
          onDecided={refreshAfterChange}
        />
      )}
      {decisionRequest?.kind === 'reject' && (
        <AccessRejectModal
          request={decisionRequest.request}
          onClose={() => setDecisionRequest(null)}
          onDecided={refreshAfterChange}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="owner-access-overlay" role="dialog" aria-modal="true" aria-label="Пользователи и доступы">
        <div className="owner-access-shell">
          <header className="owner-access-top">
            {handleClose ? (
              <button type="button" className="owner-access-btn owner-access-back" onClick={handleClose}>
                ←
              </button>
            ) : null}
            <div>
              <h1>Пользователи портала</h1>
              <p>
                Единый административный модуль: пользователи, роли, приглашения, заявки на доступ и массовое
                добавление.
              </p>
            </div>
            <span className="owner-access-spacer" />
            <button type="button" className="owner-access-btn" onClick={() => setShowBulk(true)}>
              Массовое добавление
            </button>
            <button type="button" className="owner-access-btn mint" onClick={() => setTab('access_requests')}>
              Заявки на доступ{kpi ? ` · ${kpi.pending_access_requests}` : ''}
            </button>
            <button type="button" className="owner-access-btn gold" onClick={() => setShowAdd(true)}>
              + Добавить пользователя
            </button>
          </header>

          <div className="owner-access-content">
            {kpi ? (
              <div className="owner-access-summary">
                {KPI_KEYS.map(({ key, labelKey }, index) => (
                  <article
                    key={key}
                    className="owner-access-stat"
                    style={{ ['--c' as string]: kpiColors[index % kpiColors.length] }}
                  >
                    <small>{t(labelKey as never)}</small>
                    <b>{kpi[key]}</b>
                  </article>
                ))}
              </div>
            ) : null}

            <nav className="owner-access-tabs">
              <button
                type="button"
                className={tab === 'users' ? 'active' : undefined}
                onClick={() => setTab('users')}
              >
                Пользователи
              </button>
              <button
                type="button"
                className={tab === 'access_requests' ? 'active' : undefined}
                onClick={() => setTab('access_requests')}
              >
                Заявки на доступ{kpi ? ` · ${kpi.pending_access_requests}` : ''}
              </button>
            </nav>

            {tab === 'users' ? usersPanel : requestsPanel}
          </div>
        </div>
        {modals}
      </div>
    );
  }

  return (
    <section className="dashboard workspace-page">
      <div className="dashboard-inner">
        <header className="dashboard-head">
          <div>
            <p className="breadcrumbs">{breadcrumb}</p>
            <h2>{module ? module.label[locale] : t('workspace.tab.users')}</h2>
            <p>{module ? module.description[locale] : ''}</p>
          </div>
        </header>
        {kpi ? (
          <div className="admin-kpi-grid">
            {KPI_KEYS.map(({ key, labelKey }) => (
              <div key={key} className="admin-kpi-card">
                <strong>{kpi[key]}</strong>
                <small>{t(labelKey as never)}</small>
              </div>
            ))}
          </div>
        ) : null}
        <div className="admin-tab-switch">
          <button type="button" data-active={tab === 'users'} onClick={() => setTab('users')}>
            {t('workspace.tab.users')}
          </button>
          <button
            type="button"
            data-active={tab === 'access_requests'}
            onClick={() => setTab('access_requests')}
          >
            {t('workspace.tab.access_requests')}
            {kpi ? ` (${kpi.pending_access_requests})` : ''}
          </button>
        </div>
        <div className="admin-toolbar">
          <div className="admin-toolbar-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowBulk(true)}>
              {t('admin.users.action.bulkAdd')}
            </button>
            <button type="button" className="btn-primary" onClick={() => setShowAdd(true)}>
              + {t('admin.users.action.addUser')}
            </button>
          </div>
        </div>
        {tab === 'users' ? usersPanel : requestsPanel}
      </div>
      {modals}
    </section>
  );
};

export default AdminUsersPage;
